import Button from '@ingka/button';
import Loading, { LoadingBall } from '@ingka/loading';
import Modal, { ModalBody, ModalFooter, ModalHeader, Sheets } from '@ingka/modal';
import plus from '@ingka/ssr-icon/paths/plus';
import Text from '@ingka/text';
import {
  addMessage,
  useAddNewManualParticipantsMutation,
  useAppDispatch,
  useDeleteManualParticipantMutation,
  useGetManualParticipantsQuery,
} from '@lotta/self-service/lottery-fe/data-access-state';
import { useLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import { InlineErrorMessage } from '@lotta/self-service/lottery-fe/ui-common';
import { createValidationResolver } from '@lotta/self-service/lottery-fe/util-validation-resolver';
import { useCallback, useEffect } from 'react';
import type { FieldError } from 'react-hook-form';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ControlledParticipantRow } from './ControlledParticipantRow';
import styles from './ManualParticipantsManager.module.scss';
import { ParticipantRow } from './ParticipantsRow';
import { UploadManualParticipantsButton } from './UploadManualParticipantsButton';
import type { FormData } from './types/form-data';
import { FormDataSchema } from './types/form-data';

const DEFAULT_VALUES: FormData = {
  newParticipants: [],
};

export type ManualParticipantsManagerProps = {
  visible: boolean;
  onClose: () => void;
};

export type ManualParticipantsParams = {
  lotteryId: string;
};

export const ManualParticipantsManager = ({ visible, onClose }: ManualParticipantsManagerProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { retailUnit } = useLocale();
  const { lotteryId } = useParams<ManualParticipantsParams>() as ManualParticipantsParams;
  const {
    data: manualParticipants,
    isLoading: isLoadingManualParticipants,
    isError: isErrorManualParticipants,
    isFetching: isFetchingManualParticipants,
  } = useGetManualParticipantsQuery(
    {
      retailUnit,
      lotteryId: lotteryId,
    },
    { skip: !visible }
  );
  const [
    addNewManualParticipant,
    { isLoading: isLoadingAdd, isError: isAddError, isSuccess: isAddSuccess },
  ] = useAddNewManualParticipantsMutation();
  const [deleteManualParticipant, { isLoading: isLoadingDelete, isError: isDeleteError }] =
    useDeleteManualParticipantMutation();

  const {
    handleSubmit,
    control,
    formState: { isValid },
    trigger: triggerValidation,
  } = useForm<FormData>({
    defaultValues: DEFAULT_VALUES,
    resolver: createValidationResolver<FormData>(FormDataSchema),
    mode: 'onTouched',
  });

  const {
    fields: newParticipantsFormData,
    append: appendNewParticipant,
    remove: removeNewParticipant,
  } = useFieldArray({
    name: 'newParticipants',
    control,
  });

  useEffect(() => {
    if (isAddSuccess) {
      removeNewParticipant();
      triggerValidation();
    }
  }, [isAddSuccess, removeNewParticipant, triggerValidation]);

  const onRemoveExistingParticipantsClick = (participant: {
    partyUid: string;
    entryDate: string;
  }) => deleteManualParticipant({ retailUnit, lotteryId, participant });

  const onRemoveNewParticipantsClick = (index: number) => removeNewParticipant(index);

  const onAddParticipantClick = () =>
    appendNewParticipant({ partyUid: '', entryDate: new Date().toISOString().split('T')[0] });

  const onSaveNewParticipantsClick = handleSubmit(({ newParticipants }: FormData) => {
    const newParticipantsPayload = newParticipants.map((participant) => ({
      entryDate: participant.entryDate,
      partyUid: participant.partyUid,
    }));

    addNewManualParticipant({
      retailUnit,
      lotteryId,
      newParticipants: newParticipantsPayload,
    });
  });

  const onUploadParticipants = useCallback(
    (
      participants: Array<{
        partyUid: string;
        entryDate: string;
      }>
    ) => {
      participants.forEach((participant) => {
        appendNewParticipant(participant);
      });
    },
    [appendNewParticipant]
  );

  const onUploadParticipantsError = useCallback(
    (error: FieldError) => {
      dispatch(
        addMessage({
          code: 'SOMETHING_WENT_WRONG',
          key: error.message ?? 'general-something-went-wrong',
          type: 'ERROR',
        })
      );
    },
    [dispatch]
  );

  const modalHeader = <ModalHeader title={`${t('lottery-details-manage-manual-participants')}`} />;

  const modalFooter = (
    <ModalFooter>
      <Button
        type="primary"
        text={`${t('lottery-details-manage-manual-participants-save-button')} (${
          newParticipantsFormData.length
        })`}
        onClick={onSaveNewParticipantsClick}
        disabled={!isValid || isLoadingAdd}
      />
    </ModalFooter>
  );

  const renderParticipants = () =>
    manualParticipants?.participants.map((participant) => {
      const { partyUid, entryDate } = participant;

      return (
        <ParticipantRow
          onRemoveParticipantsClick={() => onRemoveExistingParticipantsClick(participant)}
          disabled
          participant={participant}
          key={`${partyUid}-${entryDate}`}
        />
      );
    });

  const renderNewParticipants = () => {
    if (newParticipantsFormData.length === 0) {
      return null;
    }

    return newParticipantsFormData.map((newParticipant, index) => {
      return (
        <ControlledParticipantRow
          disabled={false}
          onRemoveParticipantsClick={() => onRemoveNewParticipantsClick(index)}
          index={index}
          key={newParticipant.id}
          control={control}
        />
      );
    });
  };

  const modalBody = () => {
    if (
      isLoadingManualParticipants ||
      isFetchingManualParticipants ||
      isLoadingDelete ||
      isLoadingAdd
    ) {
      return (
        <Loading text={`${t('app-loading')}`}>
          <LoadingBall />
        </Loading>
      );
    }

    if (isErrorManualParticipants || isAddError || isDeleteError) {
      return (
        <InlineErrorMessage
          title={`${t('general-error')}`}
          body={`${t('general-something-went-wrong')}`}
        />
      );
    }

    return (
      <>
        <Text>{t('lottery-details-manage-manual-participants-info')}</Text>
        <UploadManualParticipantsButton
          small
          className={styles['add-participant-button']}
          handleError={onUploadParticipantsError}
          handleParticipants={onUploadParticipants}
        />
        <Button
          small
          text={`${t('lottery-details-manage-manual-participants-add-button')}`}
          ssrIcon={plus}
          className={styles['add-participant-button']}
          onClick={onAddParticipantClick}
          disabled={isLoadingAdd}
        />
        <hr className="divider" />
        {renderNewParticipants()}
        {renderParticipants()}
      </>
    );
  };

  return (
    <Modal handleCloseBtn={onClose} visible={visible}>
      <Sheets header={modalHeader} footer={modalFooter}>
        <ModalBody>{modalBody()}</ModalBody>
      </Sheets>
    </Modal>
  );
};
